<template>
  <PageContainer title="Monitoramento de alertas">
    <div class="loading" v-if="!loaded">
      <LoadingSpinner />
    </div>

    <Box v-else space="medium" class="page-content">
      <form class="main-form" @submit.prevent="handleSubmit">
        <div class="inputs">
          <div class="inputs-row">
            <Select
              class="input-full"
              :options="schemaOptions"
              legend="Auto escola"
              placeholder="Selecione"
              :modelValue="schema"
              @update:modelValue="$event => (schema = $event)"
            />

            <Period
              legend="Data"
              :firstModelValue="initialDate"
              :secondModelValue="finalDate"
              @update:firstModelValue="$event => (initialDate = $event)"
              @update:secondModelValue="$event => (finalDate = $event)"
              :disabled="loading"
            />
          </div>

          <div class="inputs-row">
            <Select
              class="input-full"
              :options="[
                { text: 'Ativa', value: 'ativa' },
                { text: 'Bloqueada', value: 'bloqueada' },
              ]"
              legend="Status"
              placeholder="Selecione"
              :modelValue="status"
              @update:modelValue="$event => (status = $event)"
            />

            <Input
              class="input-full"
              :modelValue="aluno"
              @update:modelValue="$event => (aluno = $event)"
              legend="Aluno"
              placeholder="Nome aluno"
              :disabled="loading"
            />

            <Input
              class="input-full"
              mask="###.###.###-##"
              :modelValue="cpf"
              @update:modelValue="$event => (cpf = $event)"
              legend="CPF"
              placeholder="CPF"
              :disabled="loading"
            />
          </div>

          <div class="buttons">
            <Button
              type="button"
              @handle:click="clearInputs"
              text="Limpar"
              :fill="false"
              color="danger"
              :disabled="loading"
            >
              <CloseCircleOutline :size="18" />
            </Button>

            <Button type="submit" text="Consultar" color="primary" :disabled="loading">
              <Magnify :size="18" />
            </Button>
          </div>
        </div>
      </form>

      <div class="loading" v-if="loading">
        <LoadingSpinner />
      </div>

      <div v-else-if="classes.length" class="table-container">
        <div class="infos" v-if="selectedClasses.length">
          <Button
            type="button"
            @handle:click="showModalLiberarAula"
            text="Liberar aulas"
            color="primary"
            :disabled="loading"
          >
            <LockOpenOutline :size="18" />
          </Button>
        </div>

        <div class="table-header">
          <div>
            <StatusLegend :statusOptions="statusLegendOptions" />
          </div>

          <Paginate
            :total-pages="paginationData.lastPage"
            :current-page="paginationData.currentPage"
            @page-changed="fetchAlerts"
          />
        </div>

        <Table
          :headers="['#', 'Auto escola', 'Aluno', 'CPF', 'Placa', 'Data', 'Status', 'Ações']"
          :selectable="true"
          :selected-items="selectedClasses"
          :all-items="classes"
          @select-all="selectAll"
          @toggle-selection="toggleSelection"
        >
          <tr v-for="alert in classes" :key="alert.id_aulas_praticas">
            <td v-if="alert.status === 'Aula bloqueada por alerta'" class="checkbox">
              <label>
                <input
                  type="checkbox"
                  :checked="selectedClasses.some(sel => sel.anotacao === alert.id_anotacao_pratica)"
                  @change="toggleSelection(alert)"
                />
                <span></span>
              </label>
            </td>
            <td v-else></td>
            <td>
              <BadgeCircle v-if="alert.status == 'Aula bloqueada por alerta'" color="red" />
              <BadgeCircle v-else color="green" />
            </td>
            <td>{{ schemaOptions.find(schema => schema.value === alert.schema).text }}</td>
            <td>{{ alert.nome }}</td>
            <td>{{ utils.cpfMask(alert.cpf) }}</td>
            <td>{{ alert.placa }}</td>
            <td>{{ alert.data_aula | DateBRFilter }}</td>
            <td>{{ alert.status || ' - ' }}</td>
            <td>
              <div class="actions">
                <div>
                  <IconButton
                    @handle:click="
                      showModalAlertas(
                        alert.id_aulas_praticas,
                        alert.id_anotacao_pratica,
                        alert.schema
                      )
                    "
                    color="primary"
                    title="Detalhes"
                    :disabled="loadingActions"
                  >
                    <InformationOutline :size="19" />
                  </IconButton>
                </div>

                <div>
                  <IconButton
                    @handle:click="gerarRelatorio(alert)"
                    color="secondary"
                    title="Relátorio"
                    :disabled="loadingActions"
                  >
                    <ListBoxOutline :size="19" />
                  </IconButton>
                </div>
              </div>
            </td>
          </tr>
        </Table>
      </div>
    </Box>

    <Modal ref="alertasModal" title="Lista de alertas" v-if="selectedClass">
      <div class="alertas-modal-container">
        <div class="alertas-modal-header">
          <Button
            type="button"
            @handle:click="showModalCancelarAula"
            text="Cancelar aula"
            color="danger"
          >
            <CloseCircleOutline :size="18" />
          </Button>

          <Button
            type="button"
            @handle:click="showModalLiberarAula"
            text="Liberar aula"
            color="primary"
            :disabled="selectedClass.status != 'Aula bloqueada por alerta'"
          >
            <LockOpenOutline :size="18" />
          </Button>
        </div>

        <Tabs :options="['Alertas encontrados', 'Fotos']">
          <div slot="Alertas encontrados">
            <Table :headers="['Instrutor', 'Veículo', 'Data e Hora', 'Alerta']">
              <tr v-for="alert in alerts" :key="alert.id">
                <td>{{ alert.nomeInstrutor }}</td>
                <td>{{ alert.placa }}</td>
                <td>{{ alert.data_aula | DateBRFilter }}</td>
                <td v-if="alert.alerta_total">{{ alert.alerta }} - <strong>Tempo total parado: {{ alert.alerta_total }}</strong></td>
                <td v-else>{{ alert.alerta }}</td>
              </tr>
            </Table>
          </div>

          <div slot="Fotos" class="fotos-container">
            <div class="infos">
              <Input
                legend="Aluno"
                :modelValue="alerts[0].nomeAluno"
                readonly
                class="input-full"
              />

              <Input
                legend="Instrutor"
                :modelValue="alerts[0].nomeInstrutor"
                readonly
                class="input-full"
              />

              <Input
                legend="Início da Aula (Previsto)"
                :modelValue="utils.formatDate(aula.data_aula)"
                readonly
                class="input-full"
              />

              <Input
                legend="Fim da Aula (Previsto)"
                :modelValue="utils.formatDate(aula.data_hora_fim)"
                readonly
                class="input-full"
              />
            </div>

            <div class="empty-photos" v-if="!fotos.length">
              <span>Nenhum registro de imagens encontrado!</span>
            </div>

            <div class="fotos-row">
              <div class="fotos-content">
                <h2>
                  Total de fotos: <strong>{{ fotos.length + biometriasAluno.length + biometriasInstrutor.length }}</strong>
                </h2>

                <div class="fotos-list">
                  <div class="fotos-section">
                    <strong>Instrutor</strong>

                    <div class="fotos-body">
                      <span v-if="!biometriasInstrutor.length">Sem fotos de biometria</span>

                      <img
                        v-else
                        v-for="(foto, Index) in biometriasInstrutor"
                        :key="Index"
                        :id="foto.idFoto"
                        @click="imgSelected = foto"
                        :src="loadImage(foto.base64)"
                        alt="Fotos da aula"
                      />
                    </div>
                  </div>

                  <div class="fotos-section">
                    <strong>Aluno</strong>

                    <div class="fotos-body">
                      <span v-if="!biometriasAluno.length">Sem fotos de biometria</span>

                      <img
                        v-else
                        v-for="(foto, Index) in biometriasAluno"
                        :key="Index"
                        :id="foto.idFoto"
                        @click="imgSelected = foto"
                        :src="loadImage(foto.base64)"
                        alt="Fotos da aula"
                      />
                    </div>
                  </div>

                  <div class="fotos-section">
                    <strong>Capturas aleratórias</strong>

                    <div class="fotos-body">
                      <span v-if="!fotos.length">Sem fotos de captura aleatórias</span>

                      <img
                        v-else
                        v-for="(foto, Index) in fotos"
                        :key="Index"
                        :id="foto.idFoto"
                        @click="imgSelected = foto"
                        :src="loadImage(foto.base64)"
                        alt="Fotos da aula"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="current-img">
                <img
                  v-if="imgSelected"
                  :src="loadImage(imgSelected.base64)"
                  alt="Foto selecionada"
                />
              </div>
            </div>
          </div>
        </Tabs>
      </div>
    </Modal>

    <Modal ref="cancelarAulaModal" title="Cancelar aula">
      <form class="cancelar-form" @submit.prevent="handleCancelClass">
        <div class="inputs">
          <div class="inputs-row">
            <Textarea
              class="input-full"
              legend="Motivo do cancelamento"
              @update:modelValue="$event => (justificativaCancelar = $event)"
              :modelValue="justificativaCancelar"
              :disabled="cancelLoading"
            />
          </div>

          <div class="buttons">
            <Button type="submit" text="Confirmar" color="danger" :disabled="cancelLoading" />
          </div>
        </div>
      </form>
    </Modal>

    <Modal ref="liberarAulaModal" title="Liberar aula">
      <form class="cancelar-form" @submit.prevent="handleUnlockClass">
        <div class="inputs">
          <div class="inputs-row">
            <Textarea
              class="input-full"
              legend="Justificativa"
              @update:modelValue="$event => (justificativaLiberar = $event)"
              :modelValue="justificativaLiberar"
              :disabled="unlockLoading"
            />
          </div>

          <div class="buttons">
            <Button type="submit" text="Confirmar" color="primary" :disabled="unlockLoading" />
          </div>
        </div>
      </form>
    </Modal>
  </PageContainer>
</template>

<script>
import BadgeCircle from 'core/BadgeCircle'
import Box from 'core/Box'
import Button from 'core/Button'
import IconButton from 'core/IconButton'
import Input from 'core/Input'
import LoadingSpinner from 'core/LoadingSpinner'
import Modal from 'core/Modal'
import PageContainer from 'core/PageContainer'
import Paginate from 'core/Paginate'
import Period from 'core/Period'
import Select from 'core/Select'
import StatusLegend from 'core/StatusLegend'
import Table from 'core/Table'
import Tabs from 'core/Tabs'
import Textarea from 'core/Textarea'

import CloseCircleOutline from 'icons/CloseCircleOutline'
import InformationOutline from 'icons/InformationOutline'
import ListBoxOutline from 'icons/ListBoxOutline'
import LockOpenOutline from 'icons/LockOpenOutline'
import Magnify from 'icons/Magnify'

import utils from '@/services/utils'

export default {
  name: 'ListarAlertas',
  components: {
    PageContainer,
    LoadingSpinner,
    Box,
    Button,
    Select,
    Period,
    Input,
    Table,
    Paginate,
    IconButton,
    BadgeCircle,
    Modal,
    Tabs,
    Textarea,
    StatusLegend,

    CloseCircleOutline,
    Magnify,
    InformationOutline,
    LockOpenOutline,
    ListBoxOutline,
  },
  data() {
    return {
      utils: utils,
      schema: null,
      status: null,
      initialDate: '',
      finalDate: '',
      aluno: '',
      cpf: '',
      justificativaCancelar: '',
      justificativaLiberar: 'Aula realizada com sucesso!',
      paginationData: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
      },
      selectedClass: null,
      aula: null,
      schemaOptions: [],
      classes: [],
      alerts: [],
      selectedClasses: [],
      statusLegendOptions: [
        { legend: 'Ativa', color: 'green' },
        { legend: 'Bloqueada', color: 'red' },
      ],
      fotos: [],
      loaded: false,
      loading: false,
      loadingActions: false,
      cancelLoading: false,
      unlockLoading: false,
      imgSelected: '',
      isMoto: false,
      biometriasAluno: [],
      biometriasInstrutor: [],
    }
  },
  async mounted() {
    await this.fetchCfcs()

    this.loaded = true
  },
  methods: {
    async fetchCfcs() {
      await this.$http
        .get(`${process.env.VUE_APP_URL_API_V2}/autoescola/todas`)
        .then(res => {
          this.schemaOptions = res.body.dados.map(cfc => ({
            value: cfc.tenant_schema,
            text: cfc.nome_fantasia,
          }))
        })
        .catch(error => {
          this.$toasted.show('Erro!' + error.body.mensagem, {
            position: 'top-right',
          })
        })
    },
    clearInputs() {
      this.schema = null
      this.initialDate = ''
      this.finalDate = ''
      this.aluno = ''
      this.cpf = ''
    },
    async handleSubmit() {
      if (this.validateInputs()) {
        await this.fetchAlerts()
      }
    },
    validateInputs() {
      if (!this.initialDate && !this.finalDate) {
        this.$toasted.show('Os campos de data são obrigatórios.', {
          icon: 'fa-times',
          position: 'top-right',
          duration: 5000,
          fullWidth: false,
        })
        return false
      }

      if (
        this.initialDate &&
        this.finalDate &&
        new Date(this.initialDate) > new Date(this.finalDate)
      ) {
        this.$toasted.show('A data inicial não pode ser maior que a data final.', {
          icon: 'fa-times',
          position: 'top-right',
          duration: 5000,
          fullWidth: false,
        })
        return false
      }

      return true
    },
    async fetchAlerts(page = 1) {
      this.selectedClasses = []

      this.loading = true

      const params = {
        page,
        dateI: this.initialDate,
        dateF: this.finalDate,
      }

      if (this.schema) params.schema = this.schema
      if (this.status) params.status = this.status
      if (this.aluno) params.aluno = this.aluno
      if (this.cpf) params.cpf = this.cpf

      await this.$http
        .get(`${process.env.VUE_APP_URL_API_V2}/aulas/praticas/consultar/aulas-com-alerta`, {
          params,
        })
        .then(({ body }) => {
          const dados = body.dados

          if (!dados.data.length) {
            this.$toasted.show('Sem resultados para sua pesquisa.', {
              icon: 'fa-times',
              position: 'top-right',
              duration: 5000,
              fullWidth: false,
            })

            this.classes = []

            return
          }

          this.classes = dados.data

          this.paginationData = {
            currentPage: dados.current_page,
            lastPage: dados.last_page,
            total: dados.total,
          }
        })
        .catch(({ body }) => {
          this.$toasted.show(body.dados, {
            icon: 'fa-times',
            position: 'top-right',
            duration: 5000,
            fullWidth: false,
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async getAlertaDetails(id, schema) {
      this.loadingActions = true

      this.$http
        .get(`${process.env.VUE_APP_URL_API_V2}/aulas/praticas/consultar/alerta/${schema}/${id}`)
        .then(({ body }) => {
          this.alerts = body.dados
          this.selectedClass = {
            aluno: body.dados[0].nomeAluno,
            status: body.dados[0].status,
            anotacao: body.dados[0].id_anotacao_pratica,
            schema,
          }
        })
        .catch(({ body }) => {
          this.$toasted.show(body.mensagem, {
            icon: 'fa-times',
            position: 'top-right',
            duration: 5000,
            fullWidth: false,
          })
        })
        .finally(() => {
          this.loadingActions = false
          this.$refs.alertasModal.open()
        })
    },
    handleCancelClass() {
      if (!this.justificativaCancelar || this.justificativaCancelar.length < 6) {
        this.$toasted.show('Por favor, escreva uma justificativa de pelo menos 6 caracteres!', {
          icon: 'fa-times',
          position: 'top-right',
          duration: 5000,
          fullWidth: false,
        })

        return
      }

      this.cancelLoading = true

      const data = {
        id: this.selectedClass.anotacao,
        schema: this.selectedClass.schema,
        justificativa: this.justificativaCancelar,
      }

      this.$http
        .post(`${process.env.VUE_APP_URL_API_V2}/aulas/praticas/removerAnotacao`, data)
        .then(({ body }) => {
          this.$toasted.show('Aula cancelada!', {
            icon: 'fa-info',
            position: 'top-right',
            duration: 5000,
            fullWidth: false,
          })
        })
        .catch(({ body }) => {
          this.$toasted.show(body.msg, {
            icon: 'fa-info',
            position: 'top-right',
            duration: 5000,
            fullWidth: false,
          })
        })
        .finally(() => {
          this.closeModalCancelarAula()
          this.fetchAlerts()
          this.cancelLoading = false
        })
    },
    handleUnlockClass() {
      if (!this.justificativaLiberar || this.justificativaLiberar.length < 6) {
        this.$toasted.show('Por favor, escreva uma justificativa de pelo menos 6 caracteres!', {
          icon: 'fa-times',
          position: 'top-right',
          duration: 5000,
          fullWidth: false,
        })

        return
      }

      this.unlockLoading = true

      const data = {
        justificativa: this.justificativaLiberar,
      }

      if (this.selectedClass && !this.selectedClasses.length) {
        data.aulas = [
          {
            anotacao: this.selectedClass.anotacao,
            schema: this.selectedClass.schema,
          },
        ]
      } else {
        data.aulas = this.selectedClasses.filter(
          (value, index, self) =>
            index ===
            self.findIndex(obj => obj.anotacao === value.anotacao && obj.schema === value.schema)
        )
      }

      this.$http
        .post(`${process.env.VUE_APP_URL_API_V2}/aulas/praticas/liberar-aula`, data)
        .then(response => {
          this.$toasted.show(response.body.message, {
            icon: 'fa-info',
            position: 'top-right',
            duration: 5000,
            fullWidth: false,
          })
        })
        .catch(err => {
          this.$toasted.show(err.body.message, {
            icon: 'fa-info',
            position: 'top-right',
            duration: 5000,
            fullWidth: false,
          })
        })
        .finally(() => {
          this.selectedClasses = []
          this.closeModalLiberarAula()
          this.fetchAlerts()
          this.unlockLoading = false
        })
    },
    async showModalAlertas(id, anotacao, schema) {
      this.aula = this.classes.find(aula => aula.id_aulas_praticas === id)
      console.log(this.aula)
      await this.getFotos(id, schema)
      await this.getAlertaDetails(anotacao, schema)
    },
    showModalCancelarAula() {
      this.$refs.cancelarAulaModal.open()
    },
    showModalLiberarAula() {
      this.$refs.liberarAulaModal.open()
    },
    closeModalCancelarAula() {
      this.justificativaCancelar = ''

      this.$refs.cancelarAulaModal.close()
    },
    closeModalLiberarAula() {
      this.justificativaLiberar = 'Aula realizada com sucesso!'

      this.$refs.liberarAulaModal.close()
    },
    selectAll(isChecked) {
      if (isChecked) {
        this.selectedClasses = this.classes
          .filter(classe => classe.status === 'Aula bloqueada por alerta')
          .map(classe => ({
            anotacao: classe.id_anotacao_pratica,
            schema: classe.schema,
          }))
      } else {
        this.selectedClasses = []
      }
    },
    toggleSelection(classe) {
      const index = this.selectedClasses.findIndex(
        item => item.anotacao === classe.id_anotacao_pratica
      )

      if (index === -1) {
        this.selectedClasses.push({
          anotacao: classe.id_anotacao_pratica,
          schema: classe.schema,
        })
      } else {
        this.selectedClasses.splice(index, 1)
      }
    },
    gerarRelatorio(alert) {
      const { href } = this.$router.resolve({
        name: 'RelatorioContestacaoAulaRealizada',
        params: { autoescola: alert.schema, aula: alert.id_aulas_praticas },
      })

      window.open(href, '_blank')
    },
    async getFotos(id, schema) {
      await this.$http
        .post(`${process.env.VUE_APP_URL_API_V2}/aulas/praticas/consultar/fotos/id/${id}`, {
          schema,
          aula: id,
        })
        .then(({ body }) => {
          this.isMoto = body.tipo_veiculo == 'moto' ? true : false

          this.biometriasInstrutor = body.dados.filter(
            item => item.biometria && item.entidade === 'instrutor'
          )
          this.biometriasAluno = body.dados.filter(
            item => item.biometria && item.entidade === 'aluno'
          )
          this.fotos = body.dados.filter(item => !item.biometria)
          this.imgSelected = this.biometriasAluno.length ? this.biometriasAluno[0] : this.fotos[0]
        })
        .catch(err => {
          this.$toasted.show(err.body.mensagem, {
            icon: 'fa-info',
            position: 'top-right',
            duration: 5000,
            fullWidth: false,
          })
        })
    },
    loadImage(base64String) {
      const byteCharacters = atob(base64String)
      const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0))
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray])

      const mimeType = blob.type || 'image/*'

      const dataUrl = `data:${mimeType};base64,${base64String}`
      return dataUrl
    },
  },
}
</script>

<style lang="scss" scoped>
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
}

.cancelar-form,
.main-form {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  .inputs {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .inputs-row {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem;

      .input-full {
        flex: 1;
      }

      .input-processo {
        max-width: 26.625rem;
      }
    }
  }

  .buttons {
    align-self: flex-end;

    display: flex;
    gap: 0.5rem;
  }
}

.page-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  .table-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .table-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .actions {
      display: flex;
      align-items: center;

      div {
        padding: 0 0.5rem;
        display: flex;
        justify-content: center;

        &:not(:first-child) {
          border-left: 1px solid #ececec;
        }

        &:not(:last-child) {
          border-right: 1px solid #ececec;
        }
      }
    }
  }
}

.alertas-modal-container {
  width: 80vw;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  .alertas-modal-header {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    button {
      flex: 1;
      justify-content: center;
    }
  }

  .fotos-container {
    display: flex;
    flex-direction: column;
    gap: 3rem;

    .infos {
      display: flex;
      gap: 1rem;

      .input-full {
        flex: 1;
      }
    }

    .empty-photos {
      display: flex;
      justify-content: center;
      align-items: center;

      span {
        font-size: 1.25rem;
        font-weight: 600;
        color: #1c2d5a;
      }
    }
    .fotos-row {
      display: flex;
      gap: 1rem;

      .fotos-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 1rem;

        h2 {
          margin: 0;
          font-weight: 600;
          font-size: 0.75rem;
          color: #1c2d5a;
        }

        .fotos-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          max-height: 37.5rem;
          overflow: auto;

          .fotos-section {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;

            strong {
              margin: 0;
              font-weight: 600;
              font-size: 1rem;
              color: #1c2d5a;
            }

            .fotos-body {
              display: flex;
              flex-wrap: wrap;
              gap: 0.5rem;

              img {
                width: 6.875rem;
                border: 1px solid #a8a8a8;
                border-radius: 10px;
                cursor: pointer;

                &:hover {
                  border-color: #1c2d5a;
                }
              }
            }
          }
        }
      }

      .current-img {
        display: flex;
        justify-content: center;
        align-items: center;

        img {
          width: 20.625rem;
          border: 1px solid #a8a8a8;
          border-radius: 10px;
        }
      }
    }
  }
}

.cancelar-form {
  width: 50vw;
}
</style>
